import { Component, type ReactNode } from 'react'

interface Props {
  moduleName: string
  children: ReactNode
}

interface State {
  hasError: boolean
  message: string
}

/** The browsers' words for a page's code that could not be fetched: Chrome, Safari, Firefox. */
export function isCodeLoadFailure(message: string): boolean {
  return /dynamically imported module|Importing a module script failed|error loading dynamically imported/i.test(
    message,
  )
}

/**
 * Contains a failure inside one module rather than blanking the whole portal.
 *
 * This matters more here than in a single-app portal: a federated remote is fetched over the
 * network at runtime, so "the module failed to load" is a routine condition (the remote is down,
 * the version skewed, the URL is wrong) and must not take the shell's navigation with it.
 */
export class ModuleErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, message: '' }

  static getDerivedStateFromError(err: unknown): State {
    return { hasError: true, message: err instanceof Error ? err.message : 'Unknown error' }
  }

  render() {
    if (!this.state.hasError) return this.props.children

    return (
      <div
        role="alert"
        className="flex h-full flex-col items-center justify-center gap-md p-2xl text-fg-secondary"
      >
        <p className="text-lg font-medium text-fg">
          Failed to load module: <span className="font-sans">{this.props.moduleName}</span>
        </p>
        <p className="text-sm text-fg-muted">{this.state.message}</p>
        <button
          type="button"
          className="rounded-md bg-btn-primary-bg px-lg py-sm text-sm text-btn-primary-fg transition-colors hover:bg-btn-primary-hover"
          onClick={() =>
            // React keeps a page whose code failed to load as failed, so only a fresh page (which
            // also picks up a newer build) can try that again; anything else is tried in place.
            isCodeLoadFailure(this.state.message)
              ? window.location.reload()
              : this.setState({ hasError: false, message: '' })
          }
        >
          Retry
        </button>
      </div>
    )
  }
}
